import BasePayThemPayerApi from "./basePayThemPayerApi";
import { IntentToPayTypeRequest } from "../../../models/paymentProcess/intentToPayRequest";
import { IntentToPayTypeResult } from "../../../models/paymentProcess/intentToPayResult";

import IntentToPayRequestProviderData from "../../../models/paymentProcess/intentToPayRequestProviderData";
import IntentToPayResultProviderData from "../../../models/paymentProcess/intentToPayResultProviderData";
import CompletePayRequestProviderData from "../../../models/paymentProcess/completePayRequestProviderData";
import CompletePayResultProviderData from "../../../models/paymentProcess/completePayResultProviderData";
import PaymentTransactionSummary from "../../../models/paymentTransactions/paymentTransactionSummary";
import { CompletePayTypeRequest } from "../../../models/paymentProcess/completePayRequest";
import { CompletePayTypeResult } from "../../../models/paymentProcess/completePayResult";
import { UpdateIntentToPayRequest } from "../../../models/paymentProcess/updateIntentToPayRequest";
import { PaymentJourneyStatus } from "../../../models/paymentProcess/payment-journey-status";
import { SurchargeQuoteRequest } from "../../../models/paymentFees/surcharge/surchargeQuoteRequest";
import { SurchargeQuote } from "../../../models/paymentFees/surcharge/surchargeQuote";
import { StartPayRequest } from "../../../models/paymentFees/surcharge/startPayRequest";
import { StartPayResult } from "../../../models/paymentFees/surcharge/startPayResult";
import { ChargeStoredPaymentMethodRequest } from "../../../models/paymentFees/surcharge/chargeStoredPaymentMethodRequest";
import { ChargeStoredPaymentMethodResult } from "../../../models/paymentFees/surcharge/chargeStoredPaymentMethodResult";

class PaymentProcessApi extends BasePayThemPayerApi {
  private endPoint = "payment-process";

  /*
   * Registers the payment intent with the server
   */
  public async registerPaymentIntent<
    TProviderDataRequest extends IntentToPayRequestProviderData,
    TProviderDataResult extends IntentToPayResultProviderData
  >(intentToPayRequest: IntentToPayTypeRequest<TProviderDataRequest>): Promise<IntentToPayTypeResult<TProviderDataResult>> {
    const url = `${this.endPoint}/register-payment-intent`;
    const result = await this.postWithResponse<
      IntentToPayTypeRequest<TProviderDataRequest>,
      IntentToPayTypeResult<TProviderDataResult>
    >(url, intentToPayRequest);
    return result;
  }

  public async updatePaymentIntent(updateIntentToPayRequest: UpdateIntentToPayRequest) {
    const url = `${this.endPoint}/update-payment-intent`;
    await this.post(url, updateIntentToPayRequest);
  }

  /*
   * Informs PayThem to complete the payment
   */
  public async completePayProcess<
    TProviderDataRequest extends CompletePayRequestProviderData,
    TProviderDataResult extends CompletePayResultProviderData
  >(completePayRequest: CompletePayTypeRequest<TProviderDataRequest>): Promise<CompletePayTypeResult<TProviderDataResult>> {
    const url = `${this.endPoint}/complete-payment-intent`;
    const result = await this.postWithResponse<
      CompletePayTypeRequest<TProviderDataRequest>,
      CompletePayTypeResult<TProviderDataResult>
    >(url, completePayRequest);
    return result;
  }

  /*
   * Cancels the currently registered payment intent
   */
  public async cancelPaymentIntent(customerTransactionId: string): Promise<void> {
    const url = `${this.endPoint}/cancel-payment-intent`;
    await this.post(url, { customerTransactionId });
  }

  /*
   * Returns the status of the current payment process for this session
   */
  public async getPaymentStatus(): Promise<PaymentJourneyStatus> {
    const url = `${this.endPoint}/status`;
    const result = await this.get<PaymentJourneyStatus>(url);
    return result;
  }

  /*
   * Returns any payment transaction summary
   */
  public async getPaymentTransactionSummary(): Promise<PaymentTransactionSummary> {
    const url = `${this.endPoint}/payment-transaction`;
    const result = await this.get<PaymentTransactionSummary>(url);
    return result;
  }

  /*
   * Informs PayThem to continue processing this in the background and send an email to the user
   */
  public async continueProcess(): Promise<void> {
    const url = `${this.endPoint}/continue-processing`;
    await this.post(url);
  }

  /*
   * Quotes the surcharge for the payer's card. Undefined (204) when the payment's surcharge isn't quoted.
   */
  public async quoteSurcharge(request: SurchargeQuoteRequest): Promise<SurchargeQuote | undefined> {
    const url = `${this.endPoint}/surcharge-quote`;
    const result = await this.postWithResponse<SurchargeQuoteRequest, SurchargeQuote | "">(url, request);
    // A 204 has an empty body
    return result || undefined;
  }

  /*
   * Asks whether a typed card can be charged with the quote the payer saw
   */
  public async startPay(request: StartPayRequest): Promise<StartPayResult> {
    const url = `${this.endPoint}/start-pay`;
    const result = await this.postWithResponse<StartPayRequest, StartPayResult>(url, request);
    return result;
  }

  /*
   * Charges a saved card with the quote the payer saw
   */
  public async chargeStoredPaymentMethod<
    TProviderDataRequest extends IntentToPayRequestProviderData,
    TProviderDataResult extends IntentToPayResultProviderData
  >(request: ChargeStoredPaymentMethodRequest<TProviderDataRequest>): Promise<ChargeStoredPaymentMethodResult<TProviderDataResult>> {
    const url = `${this.endPoint}/charge-stored-payment-method`;
    const result = await this.postWithResponse<
      ChargeStoredPaymentMethodRequest<TProviderDataRequest>,
      ChargeStoredPaymentMethodResult<TProviderDataResult>
    >(url, request);
    return result;
  }
}

export default PaymentProcessApi;
